/**
 * impostor — OCTAHEDRAL IMPOSTORS for the far foliage ring (perf frontier
 * 2026-09-08, item 5). A tree beyond the near ring is one billboard quad whose
 * texture is the tree itself rendered from the view direction: at load the
 * species' LOD1 mesh is baked once from an N×N grid of directions over the
 * upper hemisphere (hemi-octahedral mapping: the hemisphere unfolds onto a
 * square, every grid cell a direction) into one atlas; at draw time the
 * fragment picks the grid cell nearest its own view direction. ~15k
 * triangles per tree become 2, the atlas is shared per species, and the
 * quads are instanced per species — the technique of Amplify Impostors /
 * the three.js forest showcase, in its simplest (nearest-frame) form.
 *
 * Pure parts (harness): the octahedral encode/decode and the frame grid.
 * Baking needs a renderer (three's WebGPURenderer, WebGL2 fallback too).
 */
import * as THREE from 'three/webgpu';
import { texture as tslTexture, uv, vec2, vec3, vec4, float, positionWorld, cameraPosition, cameraViewMatrix, normalWorld, normalize, floor, clamp, abs, sign, select, Fn, If, Discard } from 'three/tsl';
// vegetation §5.2 (surfaces agent, 2026-09-27): the impostor DEPTH channel + the card's depth offset + the dithered frame pick
import { uniform, dot, length, dFdx, positionView, cameraNear, cameraFar, viewZToPerspectiveDepth, viewZToOrthographicDepth, screenCoordinate, fract, time } from 'three/tsl';
import * as TSLAll from 'three/tsl';
// r185 names @types/three 0.182 does not declare (present in node_modules/three/src/nodes/display/ViewportDepthNode.js)
type DepthFn = (viewZ: unknown, near: unknown, far: unknown) => ReturnType<typeof float>;
const { viewZToReversedPerspectiveDepth, viewZToReversedOrthographicDepth } = TSLAll as unknown as { viewZToReversedPerspectiveDepth: DepthFn; viewZToReversedOrthographicDepth: DepthFn };
import { applySeasonTint, jitterRgb } from './instancing.js';
import { makeAnchorGroup, onRenderAnchor, toAnchorLocal } from './local_origin.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';

/**
 * §5.2 THE IMPOSTOR DEPTH (surfaces agent, 2026-09-27; Brucks' octahedral impostors, the yummers hemi-octahedral write-up):
 * a far tree was a FLAT camera-facing card through its bounding-sphere centre, so where the ground rises toward the
 * viewer (every slope at the horizon) the card's lower half fell behind the terrain in the depth test and the tree
 * "sank" into the hill. The normal atlas' alpha (1, unused) now carries each texel's DEPTH along the frame's view
 * direction — d = (p − centre)·v̂ / radius ∈ [−1, 1], stored ×0.5 + 0.5 (the empty background clears to 0 = the back)
 * — and the card writes the fragment's depth moved toward the camera by d × radius × the instance's scale, so the
 * canopy's front stands in front of the slope and its back behind it, as the real tree would.
 */
export function impostorDepthEncode(d: number): number { return Math.min(1, Math.max(0, d * 0.5 + 0.5)); }
export function impostorDepthDecode(a: number): number { return a * 2 - 1; }
/** The depth offset's strength (1 = the tree's own depth; 0 = the flat card) — the probe's A/B lever. */
export const uImpostorDepth = uniform(1);
export function setImpostorDepth(on: boolean): void { (uImpostorDepth as unknown as { value: number }).value = on ? 1 : 0; }

/** Frames per atlas side (N×N views). */
export const IMPOSTOR_FRAMES = 8;
/** Atlas edge (px); each frame is ATLAS / FRAMES square. */
export const IMPOSTOR_ATLAS_PX = 1024;

/** Hemi-octahedral encode: a unit direction with y ≥ 0 → [0,1]² (Lagarde/Sousa). */
export function hemiOctEncode(x: number, y: number, z: number): [number, number] {
  const l1 = Math.abs(x) + Math.abs(y) + Math.abs(z) || 1;
  const px = x / l1, pz = z / l1; // y ≥ 0 hemisphere: (px, pz) with |px| + |pz| ≤ 1
  // Rotate the diamond into the square.
  const u = (px + pz) * 0.5 + 0.5;
  const v = (pz - px) * 0.5 + 0.5;
  return [Math.min(1, Math.max(0, u)), Math.min(1, Math.max(0, v))];
}

/** Hemi-octahedral decode: [0,1]² → unit direction with y ≥ 0. */
export function hemiOctDecode(u: number, v: number): [number, number, number] {
  const a = u * 2 - 1, b = v * 2 - 1;
  const px = (a - b) * 0.5, pz = (a + b) * 0.5;
  const py = 1 - Math.abs(px) - Math.abs(pz);
  const l = Math.hypot(px, py, pz) || 1;
  return [px / l, Math.max(0, py) / l, pz / l];
}

/** The view direction baked into frame (i, j) of an N×N grid (cell centres). */
export function frameDirection(i: number, j: number, frames = IMPOSTOR_FRAMES): [number, number, number] {
  return hemiOctDecode((i + 0.5) / frames, (j + 0.5) / frames);
}

/** The frame (i, j) whose direction is nearest to a view direction. */
export function frameFor(x: number, y: number, z: number, frames = IMPOSTOR_FRAMES): [number, number] {
  const [u, v] = hemiOctEncode(x, Math.max(0, y), z);
  return [Math.min(frames - 1, Math.floor(u * frames)), Math.min(frames - 1, Math.floor(v * frames))];
}

/**
 * THREE-FRAME BLEND (render wave 1, 2026-09-17). The continuous frame
 * coordinate `g = (u, v)·N − ½` puts frame centres on the integer lattice;
 * the grid cell around `g` splits into two triangles along its diagonal and
 * the fragment blends the three frames of the triangle it falls in with the
 * barycentric weights — continuous across every edge, so an orbiting camera
 * never sees a far tree FLIP from one baked view to the next. Frames clamp to
 * the grid (a direction past the last centre keeps that frame's weight).
 * `createImpostorMaterial` evaluates exactly this in TSL.
 */
export function frameTriangle(u: number, v: number, frames = IMPOSTOR_FRAMES): { frames: [[number, number], [number, number], [number, number]]; weights: [number, number, number] } {
  const cl = (x: number): number => Math.max(0, Math.min(frames - 1, x));
  const gx = u * frames - 0.5, gy = v * frames - 0.5;
  const cx = Math.floor(gx), cy = Math.floor(gy);
  const fx = gx - cx, fy = gy - cy;
  if (fx + fy < 1) return { frames: [[cl(cx), cl(cy)], [cl(cx + 1), cl(cy)], [cl(cx), cl(cy + 1)]], weights: [1 - fx - fy, fx, fy] };
  return { frames: [[cl(cx + 1), cl(cy + 1)], [cl(cx + 1), cl(cy)], [cl(cx), cl(cy + 1)]], weights: [fx + fy - 1, 1 - fy, 1 - fx] };
}

export interface ImpostorAtlas {
  /** UNLIT albedo (sRGB) + coverage alpha, per frame (visual-realism R4c, 2026-09-25). */
  texture: THREE.Texture;
  /** WORLD normals (linear, ×0.5 + 0.5) per frame, or null for a colour-only (legacy) bake. */
  normals: THREE.Texture | null;
  /** Bounding-sphere radius of the source (m) — the quad's half size. */
  radius: number;
  /** World-space centre offset of the source's bounding sphere (m). */
  centerY: number;
  frames: number;
  dispose(): void;
}

/**
 * Bake a source object into impostor atlases (visual-realism R4c, 2026-09-25 — "impostors must not stay
 * noon-lit"). The old bake rendered the tree LIT under a fixed rig (ambient 1.6 + a sun at (0.4, 1, 0.3))
 * and the quad was lit AGAIN with its flat normal: double-lit, and noon-shaded at sunset. Now two atlases
 * of N×N orthographic views over the upper hemisphere, the octahedral-impostor recipe:
 *   · ALBEDO — the source's own base colour × map, UNLIT (a basic node material per source material),
 *     alpha = coverage (the source's alpha test survives);
 *   · NORMALS — the source's WORLD normals (its normal map applied), ×0.5 + 0.5, alpha 1.
 * The runtime material samples both and lets the SCENE light the texel: the real sun at its real angle
 * and colour, the IBL, the night. Instances are never rotated, so the tree's object frame IS the world.
 */
export function bakeImpostorAtlas(renderer: THREE.WebGPURenderer, source: THREE.Object3D, frames = IMPOSTOR_FRAMES, atlasPx = IMPOSTOR_ATLAS_PX): ImpostorAtlas {
  const box = new THREE.Box3().setFromObject(source);
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const radius = Math.max(0.1, sphere.radius);
  const center = sphere.center.clone();
  const mkTarget = (srgb: boolean): THREE.RenderTarget => {
    const rt = new THREE.RenderTarget(atlasPx, atlasPx, { format: THREE.RGBAFormat, type: THREE.UnsignedByteType, depthBuffer: true, colorSpace: srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace });
    rt.texture.minFilter = THREE.LinearMipmapLinearFilter;
    rt.texture.magFilter = THREE.LinearFilter;
    rt.texture.generateMipmaps = true;
    return rt;
  };
  const rtA = mkTarget(true), rtN = mkTarget(false);
  const uBakeCenter = uniform(new THREE.Vector3(sphere.center.x, sphere.center.y, sphere.center.z)); // §5.2 (surfaces, 2026-09-27)
  // per source material: an unlit albedo twin and a world-normal twin
  const meshes: Array<{ mesh: THREE.Mesh; orig: THREE.Material | THREE.Material[]; albedo: THREE.Material; normal: THREE.Material }> = [];
  source.traverse((o) => {
    const mesh = o as THREE.Mesh; if (!mesh.isMesh) return;
    const src = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
    const cut = Math.max(0.35, src.alphaTest || 0.5);
    const a = new THREE.MeshBasicNodeMaterial();
    a.map = src.map ?? null; a.color.copy(src.color ?? new THREE.Color(1, 1, 1)); a.alphaTest = cut; a.side = THREE.DoubleSide; a.vertexColors = !!src.vertexColors; a.toneMapped = false;
    const nm = new THREE.MeshStandardNodeMaterial();
    nm.map = src.map ?? null; nm.normalMap = src.normalMap ?? null; if (src.normalScale) nm.normalScale.copy(src.normalScale); nm.side = THREE.DoubleSide; nm.toneMapped = false;
    const map = src.map;
    nm.fragmentNode = Fn(() => {
      if (map) If(tslTexture(map).a.lessThan(cut), () => { Discard(); });
      // §5.2: alpha = the texel's depth along this frame's view direction (impostorDepthEncode) — the camera sits on
      // the ray centre + v̂·2r, so v̂ = normalize(cameraPosition − centre).
      const vHat = normalize(cameraPosition.sub(uBakeCenter));
      const d = dot(positionWorld.sub(uBakeCenter), vHat).div(radius);
      return vec4(normalWorld.mul(0.5).add(0.5), clamp(d.mul(0.5).add(0.5), 0.0, 1.0));
    })();
    meshes.push({ mesh, orig: mesh.material, albedo: a, normal: nm });
  });
  const scene = new THREE.Scene();
  const parentBefore = source.parent;
  scene.add(source);
  const cam = new THREE.OrthographicCamera(-radius, radius, radius, -radius, 0.01, radius * 4);
  const tile = atlasPx / frames;
  const prevRT = renderer.getRenderTarget();
  // three r185 hands back a Color4 (the pinned @types/three 0.182 has no such type).
  const prevClear = new THREE.Color() as THREE.Color & { a?: number };
  (renderer as unknown as { getClearColor(t: unknown): void }).getClearColor(prevClear);
  const prevAlpha = prevClear.a ?? 1;
  const prevScissorTest = renderer.getScissorTest();
  renderer.setScissorTest(true);
  // THE TARGET'S OWN VIEWPORT (vegetation, 2026-09-26). three r185 renders into a render target through
  // `renderTarget.viewport` / `.scissor` (pixel ratio 1) — `renderer.setViewport` / `setScissor` write only the CANVAS
  // target (Renderer._renderScene). The bake set the renderer's, so each of the 64 frame renders covered the WHOLE atlas
  // and the last view overwrote the rest: every impostor sampled a 1/8 crop of one big tree (the stands drew nothing
  // recognisable — the horizon probe's unfogged frame held no tree at all). Each frame now sets the target's viewport and
  // scissor; the atlas is cleared ONCE and the renders keep what is there (autoClear off — a WebGPU pass with loadOp
  // 'clear' wipes the whole attachment whatever the scissor, so a per-frame clear would erase the frames before it).
  const prevAutoClear = renderer.autoClear;
  renderer.autoClear = false;
  for (const pass of ['albedo', 'normal'] as const) {
    for (const m of meshes) m.mesh.material = pass === 'albedo' ? m.albedo : m.normal;
    const rt = pass === 'albedo' ? rtA : rtN;
    rt.viewport.set(0, 0, atlasPx, atlasPx);
    rt.scissor.set(0, 0, atlasPx, atlasPx);
    renderer.setRenderTarget(rt);
    renderer.setClearColor(pass === 'albedo' ? 0x000000 : 0x8080ff, 0);
    renderer.clear();
    for (let j = 0; j < frames; j++) {
      for (let i = 0; i < frames; i++) {
        const [dx, dy, dz] = frameDirection(i, j, frames);
        cam.position.set(center.x + dx * radius * 2, center.y + dy * radius * 2, center.z + dz * radius * 2);
        cam.up.set(0, 1, 0);
        if (dy > 0.999) cam.up.set(0, 0, -1); // straight down: keep a stable roll
        cam.lookAt(center);
        cam.updateMatrixWorld();
        rt.viewport.set(i * tile, j * tile, tile, tile);
        rt.scissor.set(i * tile, j * tile, tile, tile);
        renderer.render(scene, cam);
      }
    }
    rt.viewport.set(0, 0, atlasPx, atlasPx);
    rt.scissor.set(0, 0, atlasPx, atlasPx);
  }
  renderer.autoClear = prevAutoClear;
  for (const m of meshes) { m.mesh.material = m.orig; m.albedo.dispose(); m.normal.dispose(); }
  renderer.setScissorTest(prevScissorTest);
  renderer.setClearColor(prevClear, prevAlpha);
  renderer.setRenderTarget(prevRT);
  const size = renderer.getSize(new THREE.Vector2());
  renderer.setViewport(0, 0, size.x, size.y);
  renderer.setScissor(0, 0, size.x, size.y);
  scene.remove(source);
  if (parentBefore) parentBefore.add(source);
  return { texture: rtA.texture, normals: rtN.texture, radius, centerY: center.y, frames, dispose: () => { rtA.dispose(); rtN.dispose(); } };
}

/**
 * The impostor material: a lit, alpha-tested quad whose colour is the atlas
 * frame nearest the fragment's own view direction (hemi-octahedral encode in
 * TSL). The quad is billboarded on the CPU (ImpostorInstancer), so the frame
 * image maps straight onto it.
 */
export function createImpostorMaterial(atlas: ImpostorAtlas, blend = false, dither = false): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial();
  // §5.2 (surfaces, 2026-09-27): DITHERED frame pick (Brucks) — instead of averaging three frames (a ghosted double
  // image where their silhouettes disagree), each pixel takes ONE frame chosen by the barycentric weights against an
  // interleaved-gradient threshold that moves every frame; the temporal resolve (TAAU) integrates the pick. One tap per
  // atlas instead of three. Only where a temporal resolve runs; else the average. The caller decides with
  // `temporal_resolve.impostorDitherFor` (gaps wave round 2): TAAU on WebGPU or TRAA — never the preset's TAAU alone,
  // which on WebGL2 builds no temporal pass and left the far woods dithering with nothing to integrate it.
  const pickDither = blend && dither;
  const frames = float(atlas.frames);
  /** The hemi-octahedral encode of the fragment's own view direction → (u, v) ∈ [0,1]². */
  const encode = (): [ReturnType<typeof float>, ReturnType<typeof float>] => {
    const d = normalize(cameraPosition.sub(positionWorld));
    const dy = clamp(d.y, 0.0, 1.0);
    const l1 = abs(d.x).add(dy).add(abs(d.z)).max(1e-4);
    const px = d.x.div(l1), pz = d.z.div(l1);
    const u = px.add(pz).mul(0.5).add(0.5);
    const v = pz.sub(px).mul(0.5).add(0.5);
    return [u as never, v as never];
  };
  const frameUv = Fn(() => {
    const [u, v] = encode();
    const fi = clamp(floor(u.mul(frames)), 0, frames.sub(1));
    const fj = clamp(floor(v.mul(frames)), 0, frames.sub(1));
    return vec2(fi, fj).add(uv()).div(frames);
  });
  // THREE-FRAME BLEND (render wave 1): the triangle of the grid cell around
  // the continuous frame coordinate, barycentric weights — `frameTriangle` is
  // the CPU mirror the harness pins. Three taps instead of one on the far ring.
  const blended = Fn(() => {
    const [u, v] = encode();
    const g = vec2(u, v).mul(frames).sub(0.5);
    const c = floor(g);
    const f = g.sub(c);
    const lower = f.x.add(f.y).lessThan(1.0);
    const f0 = select(lower, c, c.add(1.0));
    const f1 = c.add(vec2(1.0, 0.0));
    const f2 = c.add(vec2(0.0, 1.0));
    const w0 = select(lower, float(1).sub(f.x).sub(f.y), f.x.add(f.y).sub(1.0));
    const w1 = select(lower, f.x, float(1).sub(f.y));
    const w2 = select(lower, f.y, float(1).sub(f.x));
    const at = (fr: unknown) => tslTexture(atlas.texture, clamp(fr as never, 0.0, frames.sub(1)).add(uv()).div(frames) as never);
    return at(f0).mul(w0).add(at(f1).mul(w1)).add(at(f2).mul(w2));
  });
  // §5.2: the dithered pick — the same triangle and weights as `blended`, one frame per pixel.
  const ditherUv = Fn(() => {
    const [u, v] = encode();
    const g = vec2(u, v).mul(frames).sub(0.5);
    const c = floor(g);
    const f = g.sub(c);
    const lower = f.x.add(f.y).lessThan(1.0);
    const f0 = select(lower, c, c.add(1.0));
    const f1 = c.add(vec2(1.0, 0.0));
    const f2 = c.add(vec2(0.0, 1.0));
    const w0 = select(lower, float(1).sub(f.x).sub(f.y), f.x.add(f.y).sub(1.0));
    const w1 = select(lower, f.x, float(1).sub(f.y));
    // Jimenez's interleaved gradient noise on the pixel, shifted per frame (≈ 60 Hz × the 5.588 golden step)
    const px = screenCoordinate.xy.add(floor(time.mul(60)).mul(5.588238));
    const t = fract(fract(px.x.mul(0.06711056).add(px.y.mul(0.00583715))).mul(52.9829189));
    const pick = select(t.lessThan(w0), f0, select(t.lessThan(w0.add(w1)), f1, f2));
    return clamp(pick, 0.0, frames.sub(1)).add(uv()).div(frames);
  });
  const dUv = pickDither ? ditherUv() : null;
  const sample = dUv ? tslTexture(atlas.texture, dUv as never) : blend ? blended() : tslTexture(atlas.texture, frameUv() as never);
  m.colorNode = sample.rgb;
  m.opacityNode = sample.a;
  // R4c: the baked WORLD normal of the frame(s) the albedo came from, lit by the SCENE (the real sun, the IBL,
  // the night) — no baked light, no flat quad normal.
  if (atlas.normals) {
    const nt = atlas.normals;
    const nSample = dUv ? tslTexture(nt, dUv as never) : blend
      ? Fn(() => {
        const [u, v] = encode();
        const g = vec2(u, v).mul(frames).sub(0.5); const c = floor(g); const f = g.sub(c);
        const lower = f.x.add(f.y).lessThan(1.0);
        const f0 = select(lower, c, c.add(1.0)), f1 = c.add(vec2(1.0, 0.0)), f2 = c.add(vec2(0.0, 1.0));
        const w0 = select(lower, float(1).sub(f.x).sub(f.y), f.x.add(f.y).sub(1.0)), w1 = select(lower, f.x, float(1).sub(f.y)), w2 = select(lower, f.y, float(1).sub(f.x));
        const at = (fr: unknown) => tslTexture(nt, clamp(fr as never, 0.0, frames.sub(1)).add(uv()).div(frames) as never);
        return at(f0).mul(w0).add(at(f1).mul(w1)).add(at(f2).mul(w2));
      })()
      : tslTexture(nt, frameUv() as never);
    const nW = normalize(nSample.rgb.mul(2).sub(1));
    m.normalNode = normalize(cameraViewMatrix.mul(vec4(nW, 0)).xyz);
    m.roughness = 0.85;
    // §5.2 THE CARD'S DEPTH (surfaces, 2026-09-27): move the fragment along its view ray by the baked depth × the card's
    // half size in metres. The card spans 2·radius·scale over uv 0..1 and is a plane, so metres per uv unit =
    // |∂positionWorld| / |∂uv| along any screen axis (a square card: uniform scale) — no instance-matrix read needed.
    const dBaked = nSample.a.mul(2).sub(1);
    // (three calls a no-input Fn with the builder: the camera decides perspective vs orthographic depth at build time)
    m.depthNode = (Fn((builder: { camera?: { isPerspectiveCamera?: boolean }; renderer?: { reversedDepthBuffer?: boolean } }) => {
      const mPerUv = length(dFdx(positionWorld)).div(length(dFdx(uv())).max(1e-8));
      const shift = dBaked.mul(mPerUv.mul(0.5)).mul(uImpostorDepth);
      const dist = length(positionView).max(1e-4);
      const zNew = positionView.z.mul(float(1).sub(shift.div(dist)));
      // FIX (shader sweep C, 2026-10-06): viewZToPerspectiveDepth is the STANDARD mapping (three's own
      // perspectiveDepthToViewZ asks the builder, this one does not). Under reversed depth (?rz=1, WebGPU) a far card wrote
      // ~0.9999 into a buffer where larger = NEARER: every impostor beat — and then hid — everything in front of it.
      const reversed = builder.renderer?.reversedDepthBuffer === true;
      if (builder.camera?.isPerspectiveCamera === false) {
        return reversed ? viewZToReversedOrthographicDepth(zNew, cameraNear, cameraFar) : viewZToOrthographicDepth(zNew, cameraNear, cameraFar);
      }
      return reversed ? viewZToReversedPerspectiveDepth(zNew, cameraNear, cameraFar) : viewZToPerspectiveDepth(zNew, cameraNear, cameraFar);
    }) as unknown as () => ReturnType<typeof float>)();
  }
  applySeasonTint(m); // R9: the woods' season / dryness tint reaches the far trees too
  m.alphaTest = 0.45;
  m.transparent = false;
  m.roughness = 0.9;
  m.metalness = 0;
  m.side = THREE.DoubleSide;
  // Motion vectors (2026-09-12): the quads are re-aimed on the CPU and their
  // slots reused as trees cross the ring — three's frozen previous instance
  // matrices streaked every impostor on a still camera. Static pin.
  pinStaticMotionVectors(m);
  // Keep `sign`/`select`/`vec3` referenced for the TSL bundle (tree-shaken otherwise).
  void sign; void select; void vec3;
  return m;
}

/**
 * Per-species instanced billboards under the render anchor (float32 instance
 * matrices ride the floating origin like every other instancer). Quads face
 * the camera; the yaw refresh runs every REFACE_EVERY frames — far trees turn
 * slowly on screen.
 */
export class ImpostorInstancer {
  readonly mesh: THREE.InstancedMesh;
  private readonly abs = new Map<number, { x: number; y: number; z: number; s: number }>();
  private readonly slots: number[] = [];
  /** id → slot (the stand impostors add and remove thousands per update: `slots.indexOf` was O(n) a removal). */
  private readonly slotOfId = new Map<number, number>();
  private readonly free: number[] = [];
  private nextId = 1;
  private readonly anchor: { group: THREE.Group; release: () => void };
  private readonly offAnchor: () => void;
  private readonly tmpM = new THREE.Matrix4();
  private readonly tmpQ = new THREE.Quaternion();
  private readonly tmpP = new THREE.Vector3();
  private readonly tmpS = new THREE.Vector3();
  private readonly tmpAbs = new THREE.Matrix4();
  private frame = 0;
  static REFACE_EVERY = 12;

  constructor(private readonly scene: THREE.Scene, readonly atlas: ImpostorAtlas, capacity = 4096, label = 'impostor', blend = false, dither = false) {
    const geo = new THREE.PlaneGeometry(atlas.radius * 2, atlas.radius * 2);
    this.mesh = new THREE.InstancedMesh(geo, createImpostorMaterial(atlas, blend, dither), capacity);
    // R9: the far trees carry the same per-instance hue / value jitter as the near ones (a world-position hash)
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
    this.mesh.name = `impostors:${label}`;
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false; // the far ring never cast (see the shadow caster rule)
    this.mesh.receiveShadow = false;
    this.anchor = makeAnchorGroup(`anchor:${label}`);
    this.anchor.group.add(this.mesh);
    scene.add(this.anchor.group);
    this.offAnchor = onRenderAnchor(() => this.reface(true));
  }

  /** Add a tree at world (x, y, z) with uniform scale s; returns a handle (or -1 when full). */
  add(x: number, y: number, z: number, s: number): number {
    let slot: number;
    if (this.free.length) slot = this.free.pop() as number;
    else { if (this.mesh.count >= (this.mesh.instanceMatrix.count)) return -1; slot = this.mesh.count++; }
    const id = this.nextId++;
    this.slots[slot] = id;
    this.slotOfId.set(id, slot);
    this.abs.set(id, { x, y: y + this.atlas.centerY * s, z, s });
    // FIX (shader sweep C, 2026-10-06): a new card used to be written with the IDENTITY rotation (facing +Z) and only turned
    // to the camera at the next re-face — up to REFACE_EVERY frames of a card seen edge-on (a flicker as trees stream in).
    this.writeSlot(slot, id, this.hasCam ? this.lastCam : null, true);
    this.markSlot(slot);
    return id;
  }

  remove(id: number): void {
    const slot = this.slotOfId.get(id);
    if (slot === undefined) return;
    this.slotOfId.delete(id);
    this.abs.delete(id);
    // Swap-remove keeps `count` dense.
    const last = this.mesh.count - 1;
    if (slot !== last) {
      const lastId = this.slots[last];
      this.slots[slot] = lastId;
      this.slotOfId.set(lastId, slot);
      this.writeSlot(slot, lastId, this.hasCam ? this.lastCam : null, true); // the moved card keeps facing the camera (as add)
      this.markSlot(slot);
    }
    this.slots.length = last;
    this.mesh.count = last;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  get instanceCount(): number { return this.abs.size; }

  private writeSlot(slot: number, id: number, camPos: THREE.Vector3 | null, colour: boolean): void {
    const a = this.abs.get(id);
    if (!a) return;
    this.tmpP.set(a.x, a.y, a.z);
    if (camPos) {
      // Full billboard: face the camera (the baked frame was rendered from that direction).
      this.tmpM.lookAt(camPos, this.tmpP, THREE.Object3D.DEFAULT_UP);
      this.tmpQ.setFromRotationMatrix(this.tmpM);
    } else this.tmpQ.identity();
    this.tmpS.set(a.s, a.s, a.s);
    this.tmpAbs.compose(this.tmpP, this.tmpQ, this.tmpS);
    this.mesh.setMatrixAt(slot, toAnchorLocal(this.tmpAbs, this.tmpM));
    if (!colour) return; // the jitter is a hash of the tree's own position: a re-face never changes it
    const ic = this.mesh.instanceColor;
    if (ic) { const [r, g, b] = jitterRgb(a.x, a.z); ic.setXYZ(slot, r, g, b); } // ranges are marked by the callers (markSlot)
  }

  /** THE UPLOAD-RANGE LAW (bug sweep 2026-09-22): a bare needsUpdate re-uploads the WHOLE buffer
   *  (8,192 × 64 B = 512 KB a species) — for one added tree, and on every re-face. */
  private markSlot(slot: number): void {
    this.mesh.instanceMatrix.addUpdateRange(slot * 16, 16);
    this.mesh.instanceMatrix.needsUpdate = true;
    const ic = this.mesh.instanceColor; if (ic) { ic.addUpdateRange(slot * 3, 3); ic.needsUpdate = true; }
  }

  /** Re-aim every quad at the camera (every REFACE_EVERY frames, or now when forced). */
  reface(force = false, camPos?: THREE.Vector3): void {
    if (!force && (++this.frame % ImpostorInstancer.REFACE_EVERY) !== 0) return;
    if (camPos) { this.lastCam.copy(camPos); this.hasCam = true; }
    if (!this.hasCam) return;
    const cp = this.lastCam;
    // PERF (shader sweep C, 2026-10-06): a re-face rewrites the MATRICES only — it used to recompute every card's colour
    // hash and re-upload the whole live colour range every 12 frames for values that never change; and it allocated a
    // Vector3 (clone) per re-face per species.
    for (let slot = 0; slot < this.mesh.count; slot++) this.writeSlot(slot, this.slots[slot], cp, false);
    const im = this.mesh.instanceMatrix;
    im.clearUpdateRanges();
    im.addUpdateRange(0, this.mesh.count * 16); // the live slots, never the whole capacity
    im.needsUpdate = true;
  }
  private readonly lastCam = new THREE.Vector3();
  private hasCam = false;

  dispose(): void {
    this.offAnchor();
    this.anchor.release();
    this.scene.remove(this.anchor.group);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
    this.abs.clear();
  }
}
